import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  SafeAreaView,
  Dimensions,
  Platform,
  Alert,
  Animated,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import OrganizationOpportunityForm from "../components/OrganizationOpportunityForm";
import AIImportModal from "../components/AIImportModal";
import {
  getOrganizationOpportunities,
  getOrganizationStats,
  deleteOrganizationOpportunity,
  updateOrganizationOpportunity,
} from "../services/parseService";

const { width } = Dimensions.get("window");
const isDesktop = width >= 1024; // Desktop-only: require at least 1024px width

type DashboardSection = "overview" | "opportunities" | "profile" | "analytics";

interface DashboardStats {
  totalOpportunities: number;
  activeOpportunities: number;
  impressions: number;
  totalViews: number;
  recentActivity: Array<{
    type: 'opportunity' | 'view' | 'application';
    title: string;
    time: string;
    status?: string;
  }>;
}

interface OrganizationOpportunity {
  id: string;
  title: string;
  description: string;
  type: string;
  organization: string;
  location: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  startDate?: Date;
  endDate?: Date;
  applicationOpenDate?: Date;
  applicationCloseDate?: Date;
  website?: string;
  eligibility?: string;
  cost?: string;
  tags?: string[];
  gradeLevel?: string[];
  interactionTypes?: string[];
  isYearlyRecurring?: boolean;
  isClaimed?: boolean;
  isHiddenFromSearch?: boolean;
}

const OrganizationDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [activeSection, setActiveSection] = useState<DashboardSection>("overview");
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalOpportunities: 0,
    activeOpportunities: 0,
    impressions: 0,
    totalViews: 0,
    recentActivity: [],
  });
  const [opportunities, setOpportunities] = useState<OrganizationOpportunity[]>([]);
  const [showOpportunityForm, setShowOpportunityForm] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<OrganizationOpportunity | null>(null);
  const [showAIImportModal, setShowAIImportModal] = useState(false);

  // Animation reference for theme toggle
  const themeIconRotation = useRef(new Animated.Value(0)).current;

  // Check if user is organization type
  const isOrganization = currentUser && 
    (currentUser.get ? currentUser.get('userType') === 'organization' : 
     currentUser.userType === 'organization');

  useEffect(() => {
    if (isOrganization) {
      loadDashboardData();
    }
  }, [isOrganization]);

  // Animate theme icon when theme changes
  useEffect(() => {
    Animated.sequence([
      Animated.timing(themeIconRotation, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(themeIconRotation, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }),
    ]).start();
  }, [theme]);

  const getOrganizationUserId = () => {
    if (!currentUser) return null;
    return currentUser.get ? currentUser.id : currentUser.id;
  };

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const organizationUserId = getOrganizationUserId();
      if (!organizationUserId) return;

      // Load organization stats
      const stats = await getOrganizationStats(organizationUserId);
      
      // Load organization opportunities
      const orgOpportunities = await getOrganizationOpportunities(organizationUserId);
      
      // Transform opportunities to our interface
      const transformedOpportunities: OrganizationOpportunity[] = orgOpportunities.map((opp: any) => ({
        id: opp.id,
        title: opp.get('title') || '',
        description: opp.get('description') || '',
        type: opp.get('type') || '',
        organization: opp.get('organization') || '',
        location: opp.get('location') || '',
        status: opp.get('status') || 'Open',
        createdAt: opp.get('createdAt') || new Date(),
        updatedAt: opp.get('updatedAt') || new Date(),
        startDate: opp.get('startDate'),
        endDate: opp.get('endDate'),
        applicationOpenDate: opp.get('applicationOpenDate'),
        applicationCloseDate: opp.get('applicationCloseDate'),
        website: opp.get('website'),
        eligibility: opp.get('eligibility'),
        cost: opp.get('cost'),
        tags: opp.get('tags') || [],
        gradeLevel: opp.get('gradeLevel') || [],
        interactionTypes: opp.get('interactionTypes') || [],
        isYearlyRecurring: opp.get('isYearlyRecurring') || false,
        isClaimed: opp.get('isClaimed') || false,
        isHiddenFromSearch: opp.get('isHiddenFromSearch') || false,
      }));

      setOpportunities(transformedOpportunities);

      // Generate recent activity from opportunities
      const recentActivity = transformedOpportunities
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5)
        .map(opp => ({
          type: 'opportunity' as const,
          title: opp.title,
          time: getTimeAgo(opp.createdAt),
          status: opp.status
        }));

      setDashboardStats({
        ...stats,
        recentActivity
      });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  };

  // Format date for display
  const formatDate = (date: Date | undefined): string => {
    if (!date) return 'Not set';
    
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return 'Invalid date';
      
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get date status for styling
  const getDateStatus = (date: Date | undefined): 'upcoming' | 'current' | 'past' | 'none' => {
    if (!date) return 'none';
    
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return 'none';
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const targetDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
      
      if (targetDate < today) return 'past';
      if (targetDate.getTime() === today.getTime()) return 'current';
      return 'upcoming';
    } catch (error) {
      return 'none';
    }
  };

  const getOrganizationName = () => {
    if (!currentUser) return "Organization";
    
    if (currentUser.get) {
      return currentUser.get('organizationName') || currentUser.get('username') || "Organization";
    }
    
    return currentUser.organizationName || currentUser.username || "Organization";
  };

  const handleCreateOpportunity = () => {
    setEditingOpportunity(null);
    setShowOpportunityForm(true);
  };

  const handleAIImport = () => {
    setShowAIImportModal(true);
  };

  const handleEditOpportunity = (opportunity: OrganizationOpportunity) => {
    setEditingOpportunity(opportunity);
    setShowOpportunityForm(true);
  };

  const handleDeleteOpportunity = async (opportunity: OrganizationOpportunity) => {
    const organizationUserId = getOrganizationUserId();
    if (!organizationUserId) return;

    const message = `Are you sure you want to delete "${opportunity.title}"? This action cannot be undone.`;
    
    if (Platform.OS === 'web') {
      if (confirm(message)) {
        try {
          await deleteOrganizationOpportunity(opportunity.id, organizationUserId);
          await loadDashboardData(); // Refresh data
          alert('Opportunity deleted successfully!');
        } catch (error) {
          console.error('Error deleting opportunity:', error);
          alert('Failed to delete opportunity. Please try again.');
        }
      }
    } else {
      Alert.alert(
        'Delete Opportunity',
        message,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await deleteOrganizationOpportunity(opportunity.id, organizationUserId);
                await loadDashboardData(); // Refresh data
                Alert.alert('Success', 'Opportunity deleted successfully!');
              } catch (error) {
                console.error('Error deleting opportunity:', error);
                Alert.alert('Error', 'Failed to delete opportunity. Please try again.');
              }
            }
          }
        ]
      );
    }
  };

  const handleHideFromSearch = async (opportunity: OrganizationOpportunity) => {
    const organizationUserId = getOrganizationUserId();
    if (!organizationUserId) return;

    try {
      await updateOrganizationOpportunity(opportunity.id, { isHiddenFromSearch: true }, organizationUserId);
      await loadDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error hiding opportunity:', error);
      if (Platform.OS === 'web') {
        alert('Failed to hide opportunity. Please try again.');
      } else {
        Alert.alert('Error', 'Failed to hide opportunity. Please try again.');
      }
    }
  };

  const handleShowInSearch = async (opportunity: OrganizationOpportunity) => {
    const organizationUserId = getOrganizationUserId();
    if (!organizationUserId) return;

    try {
      await updateOrganizationOpportunity(opportunity.id, { isHiddenFromSearch: false }, organizationUserId);
      await loadDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error showing opportunity:', error);
      if (Platform.OS === 'web') {
        alert('Failed to show opportunity. Please try again.');
      } else {
        Alert.alert('Error', 'Failed to show opportunity. Please try again.');
      }
    }
  };

  const handleOpportunityFormSuccess = () => {
    loadDashboardData(); // Refresh data after creating/updating
  };

  const handleCloseOpportunityForm = () => {
    setShowOpportunityForm(false);
    setEditingOpportunity(null);
  };

  const handleCloseAIImportModal = () => {
    setShowAIImportModal(false);
  };

  const renderSidebar = () => (
    <View style={[
      styles.sidebar,
      { 
        backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
        borderRightColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
      },
      sidebarCollapsed && styles.sidebarCollapsed
    ]}>
      <View style={[styles.sidebarHeader, { borderBottomColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
        {!sidebarCollapsed && (
          <View style={styles.organizationInfo}>
            <View style={[styles.organizationIcon, { backgroundColor: theme === "dark" ? "#34343B" : "#F5F5F5" }]}>
              <Ionicons name="business" size={24} color={theme === "dark" ? "#FAFAFA" : "#272727"} />
            </View>
            <View style={styles.organizationDetails}>
              <Text style={[styles.organizationName, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
                {getOrganizationName()}
              </Text>
              <Text style={[styles.organizationSubtitle, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
                Organization Dashboard
              </Text>
            </View>
          </View>
        )}
        
        <TouchableOpacity
          style={[styles.collapseButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F5F5F5" }]}
          onPress={() => setSidebarCollapsed(!sidebarCollapsed)}
          activeOpacity={0.7}
        >
          <Ionicons 
            name={sidebarCollapsed ? "chevron-forward" : "chevron-back"} 
            size={16} 
            color={theme === "dark" ? "#C2C2C2" : "#555555"} 
          />
        </TouchableOpacity>
      </View>

      <View style={styles.sidebarMenu}>
        {[
          { key: 'overview', label: 'Overview', icon: 'grid-outline' },
          { key: 'opportunities', label: 'Opportunities', icon: 'list-outline' },
          { key: 'profile', label: 'Profile', icon: 'person-outline' },
          { key: 'analytics', label: 'Analytics', icon: 'analytics-outline' },
        ].map((item) => (
          <TouchableOpacity
            key={item.key}
            style={[
              styles.menuItem,
              activeSection === item.key && styles.activeMenuItem,
              { 
                backgroundColor: activeSection === item.key ? (theme === "dark" ? "#34343B" : "#F0F0F0") : "transparent",
                justifyContent: sidebarCollapsed ? "center" : "flex-start"
              }
            ]}
            onPress={() => setActiveSection(item.key as DashboardSection)}
            activeOpacity={0.7}
          >
            <Ionicons
              name={item.icon as any}
              size={20}
              color={activeSection === item.key ? "#2563EB" : (theme === "dark" ? "#C2C2C2" : "#555555")}
            />
            {!sidebarCollapsed && (
              <Text style={[
                styles.menuItemText,
                { color: activeSection === item.key ? "#2563EB" : (theme === "dark" ? "#C2C2C2" : "#555555") }
              ]}>
                {item.label}
              </Text>
            )}
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  const renderOverview = () => (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
      <View style={styles.contentHeader}>
        <Text style={[styles.contentTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
          Overview
        </Text>
        <Text style={[styles.contentSubtitle, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
          Manage your organization's opportunities
        </Text>
      </View>

      {/* Stats Grid */}
      <View style={styles.statsGrid}>
        <View style={[styles.statCard, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]}>
          <View style={[
            styles.statContent,
            { 
              backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
              borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
            }
          ]}>
            <View style={styles.statInfo}>
              <Text style={[styles.statNumber, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
                {dashboardStats.totalOpportunities}
              </Text>
              <Text style={[styles.statLabel, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
                Total Opportunities
              </Text>
            </View>
            <View style={[styles.statIcon, { backgroundColor: theme === "dark" ? "#34343B" : "#EEF2FF" }]}>
              <Ionicons name="list" size={24} color={theme === "dark" ? "#60A5FA" : "#2563EB"} />
            </View>
          </View>
        </View>

        <View style={[styles.statCard, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]}>
          <View style={[
            styles.statContent,
            { 
              backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
              borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
            }
          ]}>
            <View style={styles.statInfo}>
              <Text style={[styles.statNumber, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
                {dashboardStats.activeOpportunities}
              </Text>
              <Text style={[styles.statLabel, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
                Active
              </Text>
            </View>
            <View style={[styles.statIcon, { backgroundColor: theme === "dark" ? "#34343B" : "#F0FDF4" }]}>
              <Ionicons name="checkmark-circle" size={24} color={theme === "dark" ? "#4ADE80" : "#16A34A"} />
            </View>
          </View>
        </View>

        <View style={[styles.statCard, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]}>
          <View style={[
            styles.statContent,
            { 
              backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
              borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
            }
          ]}>
            <View style={styles.statInfo}>
              <Text style={[styles.statNumber, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
                Coming soon
              </Text>
              <Text style={[styles.statLabel, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
                Impressions
              </Text>
            </View>
            <View style={[styles.statIcon, { backgroundColor: theme === "dark" ? "#34343B" : "#F3E8FF" }]}>
              <Ionicons name="trending-up" size={24} color={theme === "dark" ? "#A78BFA" : "#8B5CF6"} />
            </View>
          </View>
        </View>

        <View style={[styles.statCard, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]}>
          <View style={[
            styles.statContent,
            { 
              backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
              borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
            }
          ]}>
            <View style={styles.statInfo}>
              <Text style={[styles.statNumber, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
                Coming Soon
              </Text>
              <Text style={[styles.statLabel, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
                Total Views
              </Text>
            </View>
            <View style={[styles.statIcon, { backgroundColor: theme === "dark" ? "#34343B" : "#F3E8FF" }]}>
              <Ionicons name="eye" size={24} color={theme === "dark" ? "#A78BFA" : "#8B5CF6"} />
            </View>
          </View>
        </View>
      </View>

      {/* Quick Actions */}
      <View style={styles.quickActionsSection}>
        <Text style={[styles.sectionTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
          Quick Actions
        </Text>
        <View style={styles.quickActionsGrid}>
          <TouchableOpacity 
            style={[
              styles.actionButton, 
              { 
                backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
                borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
              }
            ]}
            onPress={handleCreateOpportunity}
          >
            <Ionicons name="add" size={20} color={theme === "dark" ? "#60A5FA" : "#2563EB"} />
            <Text style={[styles.actionButtonText, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
              Create Opportunity
            </Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[
              styles.actionButton, 
              { 
                backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
                borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
              }
            ]}
            onPress={handleAIImport}
          >
            <Ionicons name="sparkles" size={20} color={theme === "dark" ? "#A78BFA" : "#8B5CF6"} />
            <Text style={[styles.actionButtonText, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
              AI Import
            </Text>
          </TouchableOpacity>

          <TouchableOpacity style={[
            styles.actionButton, 
            { 
              backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
              borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
            }
          ]}>
            <Ionicons name="download" size={20} color={theme === "dark" ? "#60A5FA" : "#2563EB"} />
            <Text style={[styles.actionButtonText, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
              Export Data
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Recent Activity */}
      <View style={styles.activitySection}>
        <Text style={[styles.sectionTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
          Recent Activity
        </Text>
        <View style={[
          styles.activityList, 
          { 
            backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF",
            borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB"
          }
        ]}>
          {dashboardStats.recentActivity.map((activity, index) => (
            <View key={index} style={[
              styles.activityItem,
              { borderBottomColor: theme === "dark" ? "#2A2A2E" : "#F3F4F6" }
            ]}>
              <View style={[styles.activityIcon, {
                backgroundColor: activity.type === 'opportunity' ? 
                  (theme === "dark" ? "#34343B" : "#EEF2FF") : 
                  activity.type === 'view' ? 
                  (theme === "dark" ? "#34343B" : "#F0FDF4") : 
                  (theme === "dark" ? "#34343B" : "#FEF3C7")
              }]}>
                <Ionicons 
                  name={activity.type === 'opportunity' ? 'document-text' : 
                        activity.type === 'view' ? 'eye' : 'person-add'} 
                  size={16} 
                  color={activity.type === 'opportunity' ? 
                    (theme === "dark" ? "#60A5FA" : "#2563EB") : 
                    activity.type === 'view' ? 
                    (theme === "dark" ? "#4ADE80" : "#16A34A") : 
                    (theme === "dark" ? "#FBBF24" : "#D97706")} 
                />
              </View>
              <View style={styles.activityContent}>
                <Text style={[styles.activityTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
                  {activity.title}
                </Text>
                <Text style={[styles.activityTime, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
                  {activity.time}
                </Text>
              </View>
              {activity.status && (
                <View style={[styles.statusBadge, {
                  backgroundColor: activity.status === 'Active' ? 
                    (theme === "dark" ? "#34343B" : "#F0FDF4") : 
                    (theme === "dark" ? "#34343B" : "#F1F5F9")
                }]}>
                  <Text style={[styles.statusBadgeText, {
                    color: activity.status === 'Active' ? 
                      (theme === "dark" ? "#4ADE80" : "#16A34A") : 
                      (theme === "dark" ? "#C2C2C2" : "#64748B")
                  }]}>
                    {activity.status}
                  </Text>
                </View>
              )}
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderOpportunitiesSection = () => (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
      <View style={styles.contentHeader}>
        <Text style={[styles.contentTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
          Opportunities Management
        </Text>
        <Text style={[styles.contentSubtitle, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
          Manage your organization's opportunities
        </Text>
      </View>

      {/* Action Buttons */}
      <View style={styles.actionButtonsContainer}>
        <TouchableOpacity
          style={[styles.createButton, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
          onPress={handleCreateOpportunity}
        >
          <Ionicons name="add" size={20} color={theme === "dark" ? "#60A5FA" : "#2563EB"} />
          <Text style={[styles.createButtonText, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
            Create New Opportunity
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.aiImportButton, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
          onPress={handleAIImport}
        >
          <Ionicons name="sparkles" size={20} color={theme === "dark" ? "#A78BFA" : "#8B5CF6"} />
          <Text style={[styles.aiImportButtonText, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
            AI Import from Website
          </Text>
        </TouchableOpacity>
      </View>

      {/* Opportunities List */}
      <View style={styles.opportunitiesList}>
        {opportunities.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="document-outline" size={48} color={theme === "dark" ? "#C2C2C2" : "#9CA3AF"} />
            <Text style={[styles.emptyStateTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
              No Opportunities Yet
            </Text>
            <Text style={[styles.emptyStateText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
              Create your first opportunity to get started
            </Text>
          </View>
        ) : (
          opportunities.map((opportunity) => (
            <View key={opportunity.id} style={[styles.opportunityCard, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
              <View style={styles.opportunityHeader}>
                <View style={styles.opportunityInfo}>
                  <Text style={[styles.opportunityTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
                    {opportunity.title}
                  </Text>
                  <Text style={[styles.opportunityOrganization, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
                    {opportunity.organization} • {opportunity.location}
                  </Text>
                </View>
                <View style={styles.opportunityActions}>
                  <View style={styles.statusContainer}>
                    <View style={[styles.statusBadge, {
                      backgroundColor: opportunity.status === 'Open' ? 
                        (theme === "dark" ? "#34343B" : "#F0FDF4") : 
                        opportunity.status === 'Coming Soon' ?
                        (theme === "dark" ? "#34343B" : "#FEF3C7") :
                        opportunity.status === 'Closed' ?
                        (theme === "dark" ? "#34343B" : "#FEF2F2") :
                        (theme === "dark" ? "#34343B" : "#F1F5F9")
                    }]}>
                      <Text style={[styles.statusBadgeText, {
                        color: opportunity.status === 'Open' ? 
                          (theme === "dark" ? "#4ADE80" : "#16A34A") : 
                          opportunity.status === 'Coming Soon' ?
                          (theme === "dark" ? "#FBBF24" : "#D97706") :
                          opportunity.status === 'Closed' ?
                          (theme === "dark" ? "#F87171" : "#DC2626") :
                          (theme === "dark" ? "#C2C2C2" : "#64748B")
                      }]}>
                        {opportunity.status}
                      </Text>
                    </View>
                    {opportunity.isHiddenFromSearch && (
                      <View style={[styles.hiddenBadge, { backgroundColor: theme === "dark" ? "#34343B" : "#FEF3C7" }]}>
                        <Ionicons name="eye-off" size={12} color={theme === "dark" ? "#FBBF24" : "#D97706"} />
                        <Text style={[styles.hiddenBadgeText, { color: theme === "dark" ? "#FBBF24" : "#D97706" }]}>
                          Hidden
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
              
              <Text style={[styles.opportunityDescription, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]} numberOfLines={2}>
                {opportunity.description}
              </Text>
              
              {/* Date Information */}
              <View style={[
                styles.opportunityDates,
                { backgroundColor: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }
              ]}>
                <View style={styles.dateRow}>
                  <View style={styles.dateItem}>
                    <Ionicons name="calendar-outline" size={14} color={theme === "dark" ? "#9CA3AF" : "#6B7280"} />
                    <Text style={[styles.dateLabel, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>Start:</Text>
                    <Text style={[
                      styles.dateValue, 
                      { 
                        color: getDateStatus(opportunity.startDate) === 'past' ? 
                          (theme === "dark" ? "#9CA3AF" : "#6B7280") :
                          getDateStatus(opportunity.startDate) === 'current' ?
                          (theme === "dark" ? "#FBBF24" : "#D97706") :
                          (theme === "dark" ? "#FAFAFA" : "#111827")
                      }
                    ]}>
                      {formatDate(opportunity.startDate)}
                    </Text>
                  </View>
                  <View style={styles.dateItem}>
                    <Ionicons name="calendar-outline" size={14} color={theme === "dark" ? "#9CA3AF" : "#6B7280"} />
                    <Text style={[styles.dateLabel, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>End:</Text>
                    <Text style={[
                      styles.dateValue, 
                      { 
                        color: getDateStatus(opportunity.endDate) === 'past' ? 
                          (theme === "dark" ? "#9CA3AF" : "#6B7280") :
                          getDateStatus(opportunity.endDate) === 'current' ?
                          (theme === "dark" ? "#FBBF24" : "#D97706") :
                          (theme === "dark" ? "#FAFAFA" : "#111827")
                      }
                    ]}>
                      {formatDate(opportunity.endDate)}
                    </Text>
                  </View>
                </View>
                <View style={styles.dateRow}>
                  <View style={styles.dateItem}>
                    <Ionicons name="time-outline" size={14} color={theme === "dark" ? "#9CA3AF" : "#6B7280"} />
                    <Text style={[styles.dateLabel, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>App Deadline:</Text>
                    <Text style={[
                      styles.dateValue, 
                      { 
                        color: getDateStatus(opportunity.applicationCloseDate) === 'past' ? 
                          (theme === "dark" ? "#F87171" : "#DC2626") :
                          getDateStatus(opportunity.applicationCloseDate) === 'current' ?
                          (theme === "dark" ? "#FBBF24" : "#D97706") :
                          (theme === "dark" ? "#FAFAFA" : "#111827")
                      }
                    ]}>
                      {formatDate(opportunity.applicationCloseDate)}
                    </Text>
                  </View>
                  <View style={styles.dateItem}>
                    <Ionicons name="create-outline" size={14} color={theme === "dark" ? "#9CA3AF" : "#6B7280"} />
                    <Text style={[styles.dateLabel, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>Created:</Text>
                    <Text style={[styles.dateValue, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
                      {getTimeAgo(opportunity.createdAt)}
                    </Text>
                  </View>
                </View>
              </View>
              
              <View style={styles.opportunityFooter}>
                <View style={styles.opportunityMeta}>
                  <Text style={[styles.opportunityType, { color: theme === "dark" ? "#60A5FA" : "#2563EB" }]}>
                    {opportunity.type}
                  </Text>
                </View>
                <View style={styles.opportunityButtons}>
                  <TouchableOpacity
                    style={[styles.actionButtonSmall, { backgroundColor: theme === "dark" ? "#34343B" : "#F3F4F6", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
                    onPress={() => handleEditOpportunity(opportunity)}
                  >
                    <Ionicons name="pencil" size={16} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
                    <Text style={[styles.actionButtonSmallText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
                      Edit
                    </Text>
                  </TouchableOpacity>
                  {opportunity.isHiddenFromSearch ? (
                    <TouchableOpacity
                      style={[styles.actionButtonSmall, { backgroundColor: theme === "dark" ? "#34343B" : "#F0FDF4", borderColor: theme === "dark" ? "#2A2A2E" : "#BBF7D0" }]}
                      onPress={() => handleShowInSearch(opportunity)}
                    >
                      <Ionicons name="eye" size={16} color={theme === "dark" ? "#4ADE80" : "#16A34A"} />
                      <Text style={[styles.actionButtonSmallText, { color: theme === "dark" ? "#4ADE80" : "#16A34A" }]}>
                        Show
                      </Text>
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      style={[styles.actionButtonSmall, { backgroundColor: theme === "dark" ? "#34343B" : "#FEF3C7", borderColor: theme === "dark" ? "#2A2A2E" : "#FDE68A" }]}
                      onPress={() => handleHideFromSearch(opportunity)}
                    >
                      <Ionicons name="eye-off" size={16} color={theme === "dark" ? "#FBBF24" : "#D97706"} />
                      <Text style={[styles.actionButtonSmallText, { color: theme === "dark" ? "#FBBF24" : "#D97706" }]}>
                        Hide
                      </Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={[styles.actionButtonSmall, { backgroundColor: theme === "dark" ? "#34343B" : "#FEF2F2", borderColor: theme === "dark" ? "#2A2A2E" : "#FECACA" }]}
                    onPress={() => handleDeleteOpportunity(opportunity)}
                  >
                    <Ionicons name="trash" size={16} color={theme === "dark" ? "#F87171" : "#DC2626"} />
                    <Text style={[styles.actionButtonSmallText, { color: theme === "dark" ? "#F87171" : "#DC2626" }]}>
                      Delete
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          ))
        )}
      </View>
    </ScrollView>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "overview":
        return renderOverview();
      case "opportunities":
        return renderOpportunitiesSection();
      case "profile":
        return (
          <View style={styles.content}>
            <Text style={[styles.contentTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
              Organization Profile
            </Text>
            <Text style={[styles.placeholderText, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
              Organization profile management coming soon...
            </Text>
          </View>
        );
      case "analytics":
        return (
          <View style={styles.content}>
            <Text style={[styles.contentTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
              Analytics
            </Text>
            <Text style={[styles.placeholderText, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
              Analytics dashboard coming soon...
            </Text>
          </View>
        );
      default:
        return renderOverview();
    }
  };

  // Check if screen is large enough for desktop
  if (!isDesktop) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]}>
        <View style={styles.unauthorizedContainer}>
          <Ionicons name="desktop-outline" size={48} color={theme === "dark" ? "#C2C2C2" : "#555555"} />
          <Text style={[styles.unauthorizedTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
            Desktop Required
          </Text>
          <Text style={[styles.unauthorizedText, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
            This dashboard is optimized for desktop screens. Please use a larger screen (1024px or wider) to access the organization dashboard.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!isOrganization) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]}>
        <View style={styles.unauthorizedContainer}>
          <Ionicons name="lock-closed" size={48} color={theme === "dark" ? "#C2C2C2" : "#555555"} />
          <Text style={[styles.unauthorizedTitle, { color: theme === "dark" ? "#FAFAFA" : "#272727" }]}>
            Access Restricted
          </Text>
          <Text style={[styles.unauthorizedText, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
            You need organization privileges to access this dashboard.
          </Text>
          <TouchableOpacity 
            style={[styles.loginButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F3F4F6", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
            onPress={() => {
              // Navigate to login/signup - you can customize this based on your navigation setup
              window.location.href = '/login';
            }}
          >
            <Text style={[styles.loginButtonText, { color: theme === "dark" ? "#60A5FA" : "#2563EB" }]}>
              Go to Login & Sign Up
            </Text>
            <Ionicons name="arrow-forward" size={16} color={theme === "dark" ? "#60A5FA" : "#2563EB"} />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  if (isLoading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#2563EB" />
          <Text style={[styles.loadingText, { color: theme === "dark" ? "#C2C2C2" : "#555555" }]}>
            Loading dashboard...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme === "dark" ? "#1F1F23" : "#F9FAFB" }]}>
      <View style={styles.dashboardLayout}>
        {renderSidebar()}
        <View style={styles.mainContent}>
          {renderContent()}
        </View>
      </View>
      
      {/* Theme Toggle Button - Bottom Left Corner of Screen */}
      <TouchableOpacity
        style={[
          styles.themeToggleButton,
          { 
            backgroundColor: theme === "dark" ? "#34343B" : "#F4F4F5",
            borderColor: theme === "dark" ? "#2A2A2E" : "#E4E4E7"
          }
        ]}
        activeOpacity={0.7}
        onPress={toggleTheme}
      >
        <Animated.View
          style={{
            transform: [
              {
                rotate: themeIconRotation.interpolate({
                  inputRange: [0, 1],
                  outputRange: ["0deg", "360deg"],
                }),
              },
            ],
          }}
        >
          <Ionicons
            name={theme === "dark" ? "sunny" : "moon"}
            size={20}
            color={theme === "dark" ? "#C2C2C2" : "#555555"}
          />
        </Animated.View>
      </TouchableOpacity>
      
      {/* Opportunity Form Modal */}
      {showOpportunityForm && (
        <OrganizationOpportunityForm
          opportunityId={editingOpportunity?.id || null}
          opportunityData={editingOpportunity}
          onClose={handleCloseOpportunityForm}
          onSuccess={handleOpportunityFormSuccess}
          organizationUserId={getOrganizationUserId() || ''}
        />
      )}

      {/* AI Import Modal */}
      {showAIImportModal && (
        <AIImportModal
          onClose={handleCloseAIImportModal}
          onSuccess={handleOpportunityFormSuccess}
          organizationUserId={getOrganizationUserId() || ''}
          organizationName={getOrganizationName()}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dashboardLayout: {
    flex: 1,
    flexDirection: "row",
  },
  
  // Sidebar
  sidebar: {
    width: 320,
    borderRightWidth: 1,
    paddingTop: 20,
  },
  sidebarCollapsed: {
    width: 80,
  },
  sidebarHeader: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
  },
  organizationInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  organizationIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  organizationDetails: {
    flex: 1,
  },
  organizationName: {
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  organizationSubtitle: {
    fontSize: 12,
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },
  collapseButton: {
    position: "absolute",
    right: 16,
    top: 20,
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
  },
  sidebarMenu: {
    paddingTop: 20,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginHorizontal: 12,
    borderRadius: 8,
    minHeight: 44,
  },
  activeMenuItem: {
    backgroundColor: "#F0F0F0",
  },
  menuItemText: {
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 12,
    fontFamily: "Inter-Medium",
  },

  // Main Content
  mainContent: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  contentHeader: {
    marginBottom: 32,
  },
  contentTitle: {
    fontSize: 28,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
  },
  contentSubtitle: {
    fontSize: 16,
    marginTop: 8,
    fontFamily: "Inter-Regular",
  },
  placeholderText: {
    fontSize: 16,
    fontFamily: "Inter-Regular",
  },

  // Stats Grid
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -12,
    marginBottom: 32,
  },
  statCard: {
    width: "50%",
    paddingHorizontal: 12,
    marginBottom: 16,
  },
  statContent: {
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statInfo: {
    flex: 1,
  },
  statNumber: {
    fontSize: 32,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
  },
  statLabel: {
    fontSize: 14,
    marginTop: 4,
    fontFamily: "Inter-Medium",
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },

  // Quick Actions
  quickActionsSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "600",
    marginBottom: 16,
    fontFamily: "Inter-SemiBold",
  },
  quickActionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -8,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 8,
    marginBottom: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },

  // Activity Section
  activitySection: {
    marginBottom: 32,
  },
  activityList: {
    borderRadius: 12,
    borderWidth: 1,
  },
  activityItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  activityTime: {
    fontSize: 12,
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  hiddenBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    gap: 2,
  },
  hiddenBadgeText: {
    fontSize: 10,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },

  // Loading & Error States
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    fontFamily: "Inter-Regular",
  },
  unauthorizedContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
  },
  unauthorizedTitle: {
    fontSize: 20,
    fontWeight: "600",
    marginTop: 16,
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  unauthorizedText: {
    fontSize: 16,
    textAlign: "center",
    lineHeight: 24,
    fontFamily: "Inter-Regular",
  },
  loginButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 24,
  },
  loginButtonText: {
    fontSize: 14,
    fontWeight: "500",
    marginRight: 8,
    fontFamily: "Inter-Medium",
  },

  // Opportunities Section
  actionButtonsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 24,
  },
  createButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: "600",
    marginLeft: 8,
    fontFamily: "Inter-SemiBold",
  },
  aiImportButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  aiImportButtonText: {
    fontSize: 16,
    fontWeight: "600",
    marginLeft: 8,
    fontFamily: "Inter-SemiBold",
  },
  opportunitiesList: {
    marginBottom: 32,
  },
  opportunityCard: {
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  opportunityHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  opportunityInfo: {
    flex: 1,
    marginRight: 12,
  },
  opportunityTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  opportunityOrganization: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
  },
  opportunityActions: {
    alignItems: "flex-end",
  },
  opportunityDescription: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
    fontFamily: "Inter-Regular",
  },
  opportunityDates: {
    marginBottom: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  dateRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  dateItem: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  dateLabel: {
    fontSize: 12,
    fontWeight: "500",
    marginLeft: 6,
    marginRight: 4,
    fontFamily: "Inter-Medium",
  },
  dateValue: {
    fontSize: 12,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  opportunityFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  opportunityMeta: {
    flex: 1,
  },
  opportunityType: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
  },
  opportunityDate: {
    fontSize: 12,
    fontFamily: "Inter-Regular",
  },
  opportunityButtons: {
    flexDirection: "row",
    alignItems: "center",
  },
  actionButtonSmall: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    marginLeft: 8,
  },
  actionButtonSmallText: {
    fontSize: 12,
    fontWeight: "500",
    marginLeft: 4,
    fontFamily: "Inter-Medium",
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 60,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  emptyStateText: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
    fontFamily: "Inter-Regular",
  },

  // Theme Toggle Button
  themeToggleButton: {
    position: "absolute",
    bottom: 20,
    left: 20,
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 1000,
  },
});

export default OrganizationDashboard;
